import ContentCopy from "@mui/icons-material/ContentCopy";
import Check from "@mui/icons-material/Check";
import FormatAlignLeft from "@mui/icons-material/FormatAlignLeft";
import WarningAmber from "@mui/icons-material/WarningAmber";
import { Alert, Box, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { lintGutter, linter } from "@codemirror/lint";
import { MySQL, StandardSQL, sql } from "@codemirror/lang-sql";
import { oneDark } from "@codemirror/theme-one-dark";
import type { Diagnostic } from "@codemirror/lint";
import type { Extension } from "@codemirror/state";
import ReactCodeMirror from "@uiw/react-codemirror";
import { useMemo, useState } from "react";
import { format } from "sql-formatter";
import type { SqlLanguage } from "sql-formatter";
import { Parser as SqlParser } from "node-sql-parser";
import type { SqlDialect } from "@/shared/hooks/useSqlDialect";

const DIALECT_TO_CM: Record<SqlDialect, typeof MySQL | typeof StandardSQL> = {
  mysql: MySQL,
  snowflake: StandardSQL,
};

const DIALECT_TO_FORMATTER: Record<SqlDialect, SqlLanguage> = {
  mysql: "mysql",
  snowflake: "snowflake",
};

const DIALECT_TO_NSP: Record<SqlDialect, string> = {
  mysql: "MySQL",
  snowflake: "Snowflake",
};

const sqlParserInstance = new SqlParser();

function makeSqlLinter(dialect: SqlDialect | undefined): Extension {
  return linter((view): Diagnostic[] => {
    const sqlText = view.state.doc.toString().trim();
    if (!sqlText || !dialect) return [];
    const dbType = DIALECT_TO_NSP[dialect];
    try {
      sqlParserInstance.parse(sqlText, { database: dbType });
      return [];
    } catch (error) {
      type PegError = { message?: string; location?: { start?: { offset?: number }; end?: { offset?: number } } };
      const pegError = error as PegError;
      const message = pegError.message ?? "SQL syntax error";
      const docLength = view.state.doc.length;
      const from = Math.min(pegError.location?.start?.offset ?? 0, docLength);
      const to = Math.min(pegError.location?.end?.offset ?? from + 1, docLength);
      return [{ from, to: Math.max(to, from + 1), severity: "error", message }];
    }
  });
}

type SqlCodeMirrorProps = {
  value: string;
  onChange?: (value: string) => void;
  readOnly?: boolean;
  maxHeight?: string;
  error?: string | null;
  dialect?: SqlDialect;
  label?: string;
};

export function SqlCodeMirror({
  value,
  onChange,
  readOnly = false,
  maxHeight = "300px",
  error = null,
  dialect,
  label,
}: SqlCodeMirrorProps) {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const isEditable = onChange !== undefined && !readOnly;
  const [copied, setCopied] = useState(false);

  const extensions = useMemo((): Extension[] => {
    const cmDialect = dialect ? DIALECT_TO_CM[dialect] : StandardSQL;
    const base: Extension[] = [sql({ dialect: cmDialect })];
    if (isEditable) {
      base.push(lintGutter(), makeSqlLinter(dialect));
    }
    return base;
  }, [dialect, isEditable]);

  const displayValue = useMemo(() => {
    if (isEditable || !value || !dialect) return value;
    try {
      return format(value, { language: DIALECT_TO_FORMATTER[dialect], keywordCase: "upper" });
    } catch {
      return value;
    }
  }, [value, dialect, isEditable]);

  const handleCopy = () => {
    void navigator.clipboard.writeText(displayValue).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };

  const handleFormat = () => {
    // Don't format with a guessed dialect — it can silently rewrite
    // dialect-specific syntax into something that no longer parses on save.
    if (!onChange || !dialect) return;
    try {
      const formatted = format(value, {
        language: DIALECT_TO_FORMATTER[dialect],
        keywordCase: "upper",
      });
      onChange(formatted);
    } catch {
      // leave as-is if formatter fails (e.g. incomplete SQL)
    }
  };

  return (
    <Box>
      {(label || isEditable) && (
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
          {label ? (
            <Typography
              variant="caption"
              sx={{ color: "var(--theme-text-secondary)", fontWeight: 600, fontSize: "0.75rem" }}
            >
              {label}
            </Typography>
          ) : (
            <Box />
          )}
          {isEditable && (
            <Tooltip title="Format SQL">
              <span>
                <IconButton
                  aria-label="Format SQL"
                  size="small"
                  onClick={handleFormat}
                  disabled={!dialect}
                  sx={{ p: 0.25 }}
                >
                  <FormatAlignLeft sx={{ fontSize: 16 }} />
                </IconButton>
              </span>
            </Tooltip>
          )}
        </Stack>
      )}
      <Box
        sx={{
          position: "relative",
          border: "1px solid var(--theme-border)",
          borderRadius: 1,
          overflow: "hidden",
          "& .cm-editor": { fontSize: "0.8125rem" },
          "& .cm-editor.cm-focused": { outline: "none" },
          "& .cm-scroller": { fontFamily: "monospace" },
        }}
      >
        <Tooltip title={copied ? "Copied!" : "Copy"} placement="top">
          <IconButton
            aria-label={copied ? "Copied!" : "Copy"}
            size="small"
            onClick={handleCopy}
            sx={{
              position: "absolute",
              top: 4,
              right: 4,
              zIndex: 1,
              p: 0.375,
              color: "var(--theme-text-secondary)",
              bgcolor: "var(--theme-surface)",
              border: "1px solid var(--theme-border)",
              borderRadius: 0.75,
              "&:hover": { bgcolor: "var(--theme-surface-hover)" },
            }}
          >
            {copied ? <Check sx={{ fontSize: 13 }} /> : <ContentCopy sx={{ fontSize: 13 }} />}
          </IconButton>
        </Tooltip>
        <ReactCodeMirror
          value={displayValue}
          onChange={isEditable ? onChange : undefined}
          readOnly={!isEditable}
          theme={isDark ? oneDark : "light"}
          extensions={extensions}
          maxHeight={maxHeight}
          basicSetup={{
            lineNumbers: true,
            foldGutter: false,
            dropCursor: false,
            allowMultipleSelections: false,
            indentOnInput: true,
            bracketMatching: true,
            closeBrackets: false,
            autocompletion: true,
            highlightActiveLine: isEditable,
            highlightSelectionMatches: false,
            searchKeymap: false,
          }}
        />
      </Box>
      {error && (
        <Alert
          severity="error"
          icon={<WarningAmber fontSize="inherit" />}
          sx={{ mt: 1 }}
        >
          {error}
        </Alert>
      )}
    </Box>
  );
}
